/**
 * PB-5276 — one semantic model for every Player AI failure a user can reach.
 *
 * Three rules shape this file, and each replaces something the product used to
 * do instead:
 *
 * - **A failure is classified, never transcribed.** `runFailureMessage` printed
 *   `Player AI Run failed (INVALID_RUNTIME_STATE)` for anything it did not
 *   recognise, `errorMessage` printed `${code}: ${detail}` straight from the
 *   problem envelope, and the rail printed `JSON.stringify(proposal.error)`.
 *   Codes are grouped into recovery FAMILIES here; the code itself never
 *   reaches the primary copy.
 * - **Retry is offered only where it is known to be safe.** An unmapped code is
 *   `unknown`, and `unknown` is NOT `safe` — a failure this bundle cannot name
 *   is also one whose side effects it cannot vouch for.
 * - **Diagnostics are an allowlist, never a scrub.** {@link playerAIFailureDetails}
 *   composes a fixed set of structured fields. Nothing is derived from an
 *   exception string, a provider body, a prompt, or conversation content, so no
 *   pattern has to be trusted to strip customer data that was never collected.
 *
 * Module-neutral by construction: nothing here names SOLVE, VSM, Hoshin or
 * Home, and the one host-supplied value — {@link PlayerAIFailureContext.surface}
 * — is a bounded classification, not a label the host writes.
 */

import { MAX_MESSAGE_ATTACHMENTS } from "./types.js";

/** The recovery a failure calls for. One family, one set of copy. */
export type PlayerAIFailureFamily =
  | "response_not_prepared"
  | "temporary_service"
  | "busy"
  | "session_expired"
  | "configuration"
  | "request_too_large"
  | "content_blocked"
  | "document_unavailable"
  | "integration_unavailable"
  | "stale_state"
  | "unverified_outcome"
  | "permission_denied"
  | "refused"
  | "unknown";

/**
 * The action a host should wire to the card's one primary button.
 *
 * `none` is a first-class answer, not a gap: several families have no action
 * this product can actually perform (asking an administrator, reviewing a
 * workspace), and a button with no real destination is worse than no button.
 */
export type PlayerAIRecoveryAction =
  | "retry"
  | "refresh"
  | "reconnect"
  | "sign_in"
  | "edit_request"
  | "none";

/**
 * Whether repeating the request is known to be safe.
 *
 * `unknown` is deliberately distinct from `not_applicable`. The second means
 * "repeating it would not help"; the first means "this bundle cannot say", and
 * the two must never collapse — treating an unnamed failure as retryable is
 * exactly how a half-applied change gets applied twice.
 */
export type PlayerAIRetrySafety =
  | "safe"
  | "review_first"
  | "not_applicable"
  | "unknown";

/** Where in the lifecycle the failure happened. */
export type PlayerAIFailureStage =
  | "session_setup"
  | "request"
  | "response"
  | "apply";

/**
 * Which path reported the failure.
 *
 * `read` and `write` share a stage but not their unknown-failure copy, and the
 * difference is the one thing this split exists for: retry safety is a property
 * of the OPERATION as much as of the error. A read that failed for a reason
 * this bundle cannot name still changed nothing, so asking again is safe by
 * construction; a write that failed for the same unnameable reason may or may
 * not have landed, and the only honest instruction is to look first.
 */
export type PlayerAIFailureSource =
  | "session_setup"
  | "read"
  | "write"
  | "run"
  | "proposal_apply";

/**
 * A safe, allowlisted diagnostic set. Every field is either a platform
 * identifier, a bounded enum, or a canonical token — never free text, and never
 * anything derived from a prompt, a document, a provider body or an exception.
 */
export interface PlayerAIFailureDiagnostics {
  readonly errorCode: string;
  readonly family: PlayerAIFailureFamily;
  readonly stage: PlayerAIFailureStage;
  readonly retrySafety: PlayerAIRetrySafety;
  readonly occurredAt: string;
  readonly runId?: string;
  readonly proposalId?: string;
  readonly requestId?: string;
  readonly httpStatus?: number;
  readonly attemptCount?: number;
  readonly runOutcome?: string;
  readonly runtimeRelease?: string;
  readonly surface?: PlayerAIFailureSurface;
  /** A canonical machine reason token, when the failure carried one. */
  readonly reason?: string;
}

/**
 * Which product surface the conversation belongs to.
 *
 * A CLOSED set the client derives from the scope it was given, so a host
 * cannot put a module's marketing name — or anything else — into a block the
 * user copies. Absent when the scope does not resolve one.
 */
export type PlayerAIFailureSurface =
  | "home"
  | "solve"
  | "hoshin"
  | "hoshin_workshop"
  | "vsm";

const FAILURE_SURFACES: ReadonlySet<string> = new Set<PlayerAIFailureSurface>([
  "home",
  "solve",
  "hoshin",
  "hoshin_workshop",
  "vsm",
]);

export interface PlayerAIFailure {
  readonly family: PlayerAIFailureFamily;
  readonly title: string;
  readonly message: string;
  readonly action: PlayerAIRecoveryAction;
  readonly retrySafety: PlayerAIRetrySafety;
  readonly diagnostics: PlayerAIFailureDiagnostics;
}

interface FamilyCopy {
  readonly title: string;
  readonly message: string;
  readonly action: PlayerAIRecoveryAction;
  readonly retrySafety: PlayerAIRetrySafety;
}

/**
 * The whole of the product's failure copy.
 *
 * A Record rather than a switch, per the repo's registry rule: a new family is
 * one key, and `satisfies` makes a missing one a compile error.
 */
const FAMILY_COPY = {
  response_not_prepared: {
    title: "Player AI couldn’t complete that response.",
    message:
      "Try again. If it keeps happening, try asking in a slightly different way.",
    action: "retry",
    retrySafety: "safe",
  },
  temporary_service: {
    title: "Player AI is temporarily unavailable.",
    message: "Try again in a moment.",
    action: "retry",
    retrySafety: "safe",
  },
  busy: {
    title: "Player AI is busy right now.",
    message: "Try again shortly.",
    action: "retry",
    retrySafety: "safe",
  },
  session_expired: {
    title: "Your session has expired.",
    message: "Sign in again, then send your request.",
    action: "sign_in",
    retrySafety: "not_applicable",
  },
  configuration: {
    // Covers provider credential and provider-rejection failures too: repeating
    // a request the provider refused for a configuration reason only refuses it
    // again, so this family deliberately offers no Try again.
    title: "Player AI isn’t available here yet.",
    message: "Ask an admin to check the Player AI configuration.",
    action: "none",
    retrySafety: "not_applicable",
  },
  request_too_large: {
    title: "That request is larger than Player AI can handle at once.",
    message: "Shorten it, or split it into smaller parts, and send it again.",
    action: "edit_request",
    retrySafety: "not_applicable",
  },
  content_blocked: {
    title: "Player AI couldn’t work with that content.",
    message: "Rephrase the request, then send it again.",
    action: "edit_request",
    retrySafety: "not_applicable",
  },
  document_unavailable: {
    title: "Player AI couldn’t read one of the attached files.",
    message: "Try again, or remove the file and upload it again.",
    action: "retry",
    retrySafety: "safe",
  },
  integration_unavailable: {
    title: "Player AI can’t reach that connected service right now.",
    message:
      "Try again later. If it keeps happening, ask an admin to check the connection.",
    action: "retry",
    retrySafety: "safe",
  },
  stale_state: {
    title: "Player AI is working from an older version of this workspace.",
    message: "Refresh Player AI to pick up the latest state, then try again.",
    action: "refresh",
    retrySafety: "not_applicable",
  },
  unverified_outcome: {
    // Never "nothing changed": the runtime stopped without a receipt, so the
    // one honest instruction is to look before asking again.
    title: "Player AI stopped before it could confirm what finished.",
    message: "Check the latest changes here before you ask again.",
    action: "none",
    retrySafety: "review_first",
  },
  permission_denied: {
    // These codes also describe product, tool and attachment refusals. They
    // do not establish that the caller lost access to the conversation.
    title: "Player AI couldn’t complete this request.",
    message: "Copy the error details if you need help.",
    action: "none",
    retrySafety: "not_applicable",
  },
  refused: {
    // The server rejected the request itself — a validation failure, an
    // invalid selection — before attempting any write. Unlike
    // `unverified_outcome`, this IS a case where "nothing changed" is a claim
    // the catalogue can make — for the ONE write or apply the server refused
    // ({@link REFUSED_BEFORE_EFFECT}); `sourceCopy` gives every other source
    // its own copy.
    title: "Not applied — nothing changed.",
    message: "The change was refused before anything was saved.",
    action: "none",
    retrySafety: "not_applicable",
  },
  unknown: {
    title: "Player AI couldn’t complete this request.",
    message:
      "Player AI can’t confirm what finished. Check anything it was working on before you ask again, and copy the error details if you need help.",
    action: "none",
    retrySafety: "unknown",
  },
} satisfies Record<PlayerAIFailureFamily, FamilyCopy>;

/**
 * A setup failure has no Run to retry and no request to edit, so a transient
 * cause is recovered by re-running initialization instead.
 *
 * Applied only to families whose cause is transient (`retrySafety: "safe"`).
 * An expired session, a missing configuration or a refused authorization is not
 * fixed by reconnecting, and offering it would send the reader in a circle.
 */
const SESSION_SETUP_COPY: FamilyCopy = {
  title: "Player AI couldn’t open this conversation.",
  message: "Reconnect to try again.",
  action: "reconnect",
  retrySafety: "safe",
};

/** A write whose outcome the client cannot establish. Never a retry button. */
const AMBIGUOUS_WRITE_COPY: FamilyCopy = {
  title: "Player AI couldn’t confirm whether that was saved.",
  message: "Refresh to see the current state before you try it again.",
  action: "refresh",
  retrySafety: "review_first",
};

/** The same, for an apply that had already been approved. */
const AMBIGUOUS_APPLY_COPY: FamilyCopy = {
  title: "Player AI couldn’t confirm that this change was applied.",
  message: "Check the current state before approving it again.",
  action: "none",
  retrySafety: "review_first",
};

/**
 * PB-5380: a message the server refused because it carried more files than one
 * message may. Said definitely — the refusal is decided before anything is
 * saved — and with the limit, so the reader knows what to change. No button:
 * the composer, which still holds the draft and its files, is the way to fix it.
 */
function messageAttachmentLimitCopy(limit: number): FamilyCopy {
  return {
    title: "Your message wasn’t sent. Nothing was saved.",
    message: `Player AI can take up to ${limit} files in one message. Remove some files, then send it again.`,
    action: "edit_request",
    retrySafety: "not_applicable",
  };
}

/** The diagnostics reason for {@link messageAttachmentLimitCopy}. */
const MESSAGE_ATTACHMENT_LIMIT_REASON = "message_attachment_limit";

/**
 * d18: a mention whose Run bounded admission DEFERRED (`run_deferred:
 * "capacity"` on the Send reply). A code this client sets itself, like
 * `NETWORK_UNAVAILABLE`, never one a server sends.
 */
export const RUN_DEFERRED_CODE = "RUN_DEFERRED";

/**
 * The message and its mention ARE saved, so this is not the busy refusal —
 * that copy means nothing was kept and invites a duplicate. No button: the
 * next mention, or the group's review schedule, picks the request up. Never
 * in {@link REFUSED_BEFORE_EFFECT}, because the write took effect.
 */
const RUN_DEFERRED_COPY: FamilyCopy = {
  title: "Your message is saved.",
  message: "Player AI is busy — @mention it again in a moment.",
  action: "none",
  retrySafety: "not_applicable",
};

/**
 * The unknown-failure copy, per source.
 *
 * The default entry in {@link FAMILY_COPY} is the Run case, because that is the
 * one where the platform may have done work it cannot report on. A failed read
 * did nothing and can simply be asked again; a failed write is the case that
 * must not offer a button, because re-issuing it is exactly how an operation
 * that had in fact landed gets applied twice.
 */
const UNKNOWN_COPY_BY_SOURCE: Partial<
  Record<PlayerAIFailureSource, FamilyCopy>
> = {
  read: {
    title: "Player AI couldn’t load that.",
    message: "Try again in a moment.",
    action: "retry",
    retrySafety: "safe",
  },
  session_setup: {
    title: "Player AI couldn’t open this conversation.",
    message: "Reconnect to try again.",
    action: "reconnect",
    retrySafety: "safe",
  },
  write: AMBIGUOUS_WRITE_COPY,
  proposal_apply: AMBIGUOUS_APPLY_COPY,
};

/**
 * Codes that prove the operation was REFUSED before it could take effect.
 *
 * This is the only basis on which a failed write may be described as having
 * changed nothing. Each of these is decided by the server before the write is
 * attempted — an authorization refusal, a precondition that did not hold, a
 * request the contract rejected, a bounded admission refusal — so the state the
 * caller was acting on is untouched.
 *
 * Everything else that can fail a write is ambiguous by default, INCLUDING the
 * transient families: a timeout, a dropped connection or a 5xx says nothing
 * about whether the server had already committed. `NETWORK_UNAVAILABLE` is the
 * clearest case — a `fetch` TypeError means no response was READ, not that no
 * request was accepted.
 */
export const REFUSED_BEFORE_EFFECT: ReadonlySet<string> = new Set([
  "FORBIDDEN",
  "NOT_FOUND",
  "NOT_AUTHORIZED",
  "INSUFFICIENT_PRIVILEGE",
  "UNAUTHENTICATED",
  "AUTH_SESSION_INVALID",
  "VALIDATION_FAILED",
  "PROPOSAL_SELECTION_INVALID",
  "PROPOSAL_NOT_PENDING",
  "PROPOSAL_BATCH_DECISION_REQUIRED",
  "PROPOSAL_BATCH_INVALID",
  "PROPOSAL_EXPIRED",
  "TARGET_STATE_CHANGED",
  "HOSHIN_XMATRIX_ITEM_CONFLICT",
  "WIDGET_EDITING_IN_PROGRESS",
  "IDEMPOTENCY_CONFLICT",
  "GROUP_MODE_INACTIVE",
  "RUN_ACTIVE",
  "RUN_CAPACITY_EXHAUSTED",
  "ATTACHMENT_NOT_READY",
  "INPUT_TOO_LARGE",
  "MODEL_POLICY_UNAVAILABLE",
  "AI_NOT_CONFIGURED",
  "CONVERSATION_SURFACE_UNAVAILABLE",
]);

/**
 * Every failure code this release can reach, mapped to its recovery family.
 *
 * Sources, so a future reader can re-derive it rather than trust it:
 * `services/ais/ais/errors.py` (`AisErrorCode`),
 * `services/player-ai/player_ai/agents/ais_model.py` (adapter codes),
 * `services/player-ai/player_ai/runtime_v2/tasks.py` + `workflow.py` (terminal
 * Run codes), `services/player-ai/player_ai/v2/errors.py` (HTTP problems), and
 * the integration/product codes those forward.
 *
 * An absent code is not a defect — it resolves to `unknown`, which is a safe
 * answer by construction.
 */
const FAMILY_BY_CODE: Readonly<Record<string, PlayerAIFailureFamily>> = {
  // ---- The model produced something the platform could not accept ----
  OUTPUT_VALIDATION_FAILED: "response_not_prepared",
  INVALID_RESPONSE: "response_not_prepared",
  SESSION_RESPONSE_LIMIT_EXCEEDED: "response_not_prepared",
  PROJECTED_MESSAGE_MISMATCH: "response_not_prepared",

  // ---- Transient: the platform or a dependency was briefly unavailable ----
  AIS_UNAVAILABLE: "temporary_service",
  DEPENDENCY_UNAVAILABLE: "temporary_service",
  AIS_DEPENDENCY_UNAVAILABLE: "temporary_service",
  AUTH_DEPENDENCY_UNAVAILABLE: "temporary_service",
  PRODUCT_DEPENDENCY_UNAVAILABLE: "temporary_service",
  SERVICE_NOT_READY: "temporary_service",
  PROVIDER_UNAVAILABLE: "temporary_service",
  PROVIDER_TIMEOUT: "temporary_service",
  SESSION_UNAVAILABLE: "temporary_service",
  SESSION_TIMEOUT: "temporary_service",
  STREAM_INTERRUPTED: "temporary_service",
  INVALID_STREAM_EVENT: "temporary_service",
  INTERNAL_ERROR: "temporary_service",
  SEGMENT_RETRIES_EXHAUSTED: "temporary_service",
  RUN_FAILURE_FINALIZATION_EXHAUSTED: "temporary_service",
  // A transport failure this client classified itself; see `failureCode`.
  NETWORK_UNAVAILABLE: "temporary_service",
  SERVICE_RESPONSE_UNREADABLE: "temporary_service",

  // ---- Bounded admission and rate limits: the same request, later ----
  RATE_LIMITED: "busy",
  PROVIDER_THROTTLED: "busy",
  MODEL_CAPACITY_EXHAUSTED: "busy",
  RUN_CAPACITY_EXHAUSTED: "busy",
  BUDGET_EXCEEDED: "busy",
  RUN_ACTIVE: "busy",
  // Saved, with its Run deferred: its own copy, see RUN_DEFERRED_COPY.
  [RUN_DEFERRED_CODE]: "busy",

  // ---- Identity ----
  AUTH_SESSION_INVALID: "session_expired",
  UNAUTHENTICATED: "session_expired",

  // ---- Deployment configuration; only an administrator can move these ----
  AI_NOT_CONFIGURED: "configuration",
  PROFILE_UNAVAILABLE: "configuration",
  NO_MODEL_CONNECTION: "configuration",
  MODEL_CONNECTION_INCOMPATIBLE: "configuration",
  MODEL_POLICY_UNAVAILABLE: "configuration",
  TOOL_SCHEMA_INCOMPATIBLE: "configuration",
  TOOL_CATALOGUE_MISMATCH: "configuration",
  CONNECTION_SECRET_UNAVAILABLE: "configuration",
  // Credential and provider-rejection failures sit here deliberately: a blind
  // retry spends another provider call on a refusal only a change of
  // configuration can lift.
  PROVIDER_AUTH_FAILED: "configuration",
  PROVIDER_REQUEST_REJECTED: "configuration",
  PROMPT_NOT_FOUND: "configuration",
  PROMPT_CONTEXT_MISSING: "configuration",
  PROMPT_VERSION_MISMATCH: "configuration",
  PROMPT_CHECKSUM_MISMATCH: "configuration",
  PARALLEL_TOOL_CALL_LIMIT_EXCEEDED: "configuration",
  PARALLEL_TOOL_CALLS_DISABLED: "configuration",
  INTEGRATION_EXECUTION_DISABLED: "configuration",
  CONVERSATION_SURFACE_UNAVAILABLE: "configuration",

  // ---- The request itself has to change ----
  INPUT_TOO_LARGE: "request_too_large",
  GUARDRAIL_INPUT_BLOCKED: "content_blocked",
  GUARDRAIL_OUTPUT_BLOCKED: "content_blocked",
  PROVIDER_CONTENT_FILTERED: "content_blocked",

  // ---- Attached files ----
  DOCUMENT_DEPENDENCY_UNAVAILABLE: "document_unavailable",
  DOCUMENT_WAIT_RETRIES_EXHAUSTED: "document_unavailable",
  DOCUMENT_WAIT_STATUS_UNAVAILABLE: "document_unavailable",
  ATTACHMENT_NOT_READY: "document_unavailable",

  // ---- Connected services ----
  INTEGRATION_CONNECTOR_UNAVAILABLE: "integration_unavailable",
  INTEGRATION_APPROVER_SESSION_MISSING: "integration_unavailable",

  // ---- What Player AI prepared no longer matches what is there ----
  TARGET_STATE_CHANGED: "stale_state",
  HOSHIN_XMATRIX_ITEM_CONFLICT: "stale_state",
  WIDGET_EDITING_IN_PROGRESS: "stale_state",
  STALE_OR_INCOMPATIBLE_EXECUTION: "stale_state",
  PROPOSAL_CATALOGUE_UNPINNED: "stale_state",
  PROPOSAL_INPUT_MISMATCH: "stale_state",
  PROPOSAL_EXPIRED: "stale_state",
  PROPOSAL_NOT_PENDING: "stale_state",
  PROPOSAL_BATCH_DECISION_REQUIRED: "stale_state",
  PROPOSAL_BATCH_INVALID: "stale_state",
  GROUP_MODE_INACTIVE: "stale_state",
  IDEMPOTENCY_CONFLICT: "stale_state",
  VSM_PREPARATION_SOURCE_INVALID: "stale_state",
  HOSHIN_WORKSHOP_COMMAND_UNAVAILABLE: "stale_state",

  /*
   * Deterministic runtime and contract failures.
   *
   * `INVALID_RUNTIME_STATE` is NOT `stale_state`, even though it is tempting:
   * `tasks.py` collapses SDK-contract, checkpoint and structured-output
   * failures into it and records them as deterministic for the pinned runtime
   * ("must not consume retries"). Refreshing the browser fixes none of those,
   * and the runtime publishes no receipt for whatever the segment had already
   * done, so the only claim this client can support is that it stopped without
   * confirming.
   */
  INVALID_RUNTIME_STATE: "unverified_outcome",
  INVALID_SEGMENT_RESULT: "unverified_outcome",
  RUNTIME_FAILURE: "unverified_outcome",
  UNEXPECTED_RUNTIME_FAILURE: "unverified_outcome",

  // ---- The request itself was rejected before any write was attempted ----
  VALIDATION_FAILED: "refused",
  PROPOSAL_SELECTION_INVALID: "refused",

  // ---- Authorization ----
  FORBIDDEN: "permission_denied",
  NOT_FOUND: "permission_denied",
  NOT_AUTHORIZED: "permission_denied",
  INSUFFICIENT_PRIVILEGE: "permission_denied",
};

/** The family a code belongs to; `unknown` for anything unmapped. */
export function playerAIFailureFamily(code: string): PlayerAIFailureFamily {
  return Object.hasOwn(FAMILY_BY_CODE, code)
    ? (FAMILY_BY_CODE[code] as PlayerAIFailureFamily)
    : "unknown";
}

/**
 * The copy for one family AT one source.
 *
 * The family alone cannot decide retry safety, and that is the correction this
 * function exists for: `temporary_service` is genuinely safe to repeat when it
 * failed a READ, and genuinely ambiguous when it failed a WRITE or an apply,
 * because a timeout says nothing about whether the server committed. Only a
 * code in {@link REFUSED_BEFORE_EFFECT} — one the server decided before doing
 * anything — keeps its family's own recovery on a write.
 */
function sourceCopy(
  family: PlayerAIFailureFamily,
  base: FamilyCopy,
  code: string,
  source: PlayerAIFailureSource,
): FamilyCopy {
  if (family === "unknown") {
    return UNKNOWN_COPY_BY_SOURCE[source] ?? base;
  }
  // "Nothing changed" is proven only for the ONE write or apply the server
  // refused. A Run's terminal code says nothing about what its earlier steps
  // applied, and a read or a setup had no change to refuse — so those keep the
  // copy they had before `refused` existed.
  if (
    family === "refused" &&
    source !== "write" &&
    source !== "proposal_apply"
  ) {
    return UNKNOWN_COPY_BY_SOURCE[source] ?? FAMILY_COPY.unknown;
  }
  if (
    (source === "write" || source === "proposal_apply") &&
    !REFUSED_BEFORE_EFFECT.has(code)
  ) {
    return source === "write" ? AMBIGUOUS_WRITE_COPY : AMBIGUOUS_APPLY_COPY;
  }
  if (source === "session_setup" && base.retrySafety === "safe") {
    return SESSION_SETUP_COPY;
  }
  return base;
}

const STAGE_BY_SOURCE = {
  session_setup: "session_setup",
  read: "request",
  write: "request",
  run: "response",
  proposal_apply: "apply",
} satisfies Record<PlayerAIFailureSource, PlayerAIFailureStage>;

/**
 * A canonical machine token — an error code, a failure kind, an outcome.
 *
 * Structural, not a scrub: a value is admitted because its SHAPE is that of an
 * enum member, so prose, a filename, a sentence from a provider and a stack
 * frame are all refused by the same rule rather than by pattern-matching what
 * they might contain.
 */
const CANONICAL_TOKEN = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/;

export function canonicalToken(value: unknown): string | undefined {
  return typeof value === "string" && CANONICAL_TOKEN.test(value)
    ? value
    : undefined;
}

/**
 * The reasons this bundle will put in a copied block — an ALLOWLIST, not a
 * shape test.
 *
 * A token-shaped regex is not a safety boundary: `secret_abc123` and
 * `user_jane_doe` both satisfy it, and the values that reach this field come
 * from a server payload and from connector code. Listing the reasons means a
 * new one is added deliberately, by someone who has looked at what produces it.
 *
 * Sources: `AisInferenceError.failure_kind` as raised in
 * `services/ais/ais/` and `services/player-ai/player_ai/agents/ais_model.py`.
 */
const KNOWN_REASONS: ReadonlySet<string> = new Set([
  // AIS output-validation kinds.
  "tool_schema_validation_failed",
  "invalid_tool_argument_envelope",
  "tool_arguments_not_object",
  // AIS transport/session kinds.
  "session_feature_disabled",
  "session_executor_unavailable",
  "session_timeout",
  "player_ai_websocket_disconnected",
  // Player AI's own AIS adapter kinds.
  "ais_websocket_connect_failed",
  "ais_websocket_send_failed",
  "session_continuation_without_tool_call",
  "session_tool_result_invalid",
  "session_tool_result_unknown",
  "session_tool_result_duplicate",
  "session_tool_result_missing",
  "projected_message_mismatch",
  // This bundle's own reasons, set here rather than read from a payload.
  MESSAGE_ATTACHMENT_LIMIT_REASON,
]);

/**
 * A reason worth carrying: known, and not a restatement of the code.
 *
 * Anything else is dropped rather than sanitised — there is no safe way to
 * decide, from the string alone, whether an unrecognised "reason" is an enum
 * member or a sentence about a customer.
 */
function knownReason(value: unknown, code: string): string | undefined {
  if (typeof value !== "string" || !KNOWN_REASONS.has(value)) return undefined;
  return value === code ? undefined : value;
}

/** A platform identifier: bounded, opaque, and never a name. */
const IDENTIFIER = /^[A-Za-z0-9_:.-]{1,128}$/;

function identifier(value: unknown): string | undefined {
  return typeof value === "string" && IDENTIFIER.test(value)
    ? value
    : undefined;
}

function timestamp(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0 || value.length > 64) {
    return undefined;
  }
  return Number.isNaN(Date.parse(value)) ? undefined : value;
}

function counter(value: unknown): number | undefined {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 1_000_000
    ? value
    : undefined;
}

function httpStatus(value: unknown): number | undefined {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 100 &&
    value <= 599
    ? value
    : undefined;
}

function surface(value: unknown): PlayerAIFailureSurface | undefined {
  return typeof value === "string" && FAILURE_SURFACES.has(value)
    ? (value as PlayerAIFailureSurface)
    : undefined;
}

/**
 * Everything a caller may contribute to the diagnostics block.
 *
 * Every field is validated here rather than trusted, because several of them
 * originate in a server payload this client does not own. A value that fails
 * its check is DROPPED — never coerced, never replaced with a placeholder, and
 * never invented.
 */
export interface PlayerAIFailureContext {
  readonly runId?: unknown;
  readonly proposalId?: unknown;
  readonly requestId?: unknown;
  readonly httpStatus?: unknown;
  readonly attemptCount?: unknown;
  readonly runOutcome?: unknown;
  readonly runtimeRelease?: unknown;
  readonly surface?: unknown;
  readonly reason?: unknown;
  /**
   * The file limit a refused message hit, from {@link playerAIFailureCode}.
   * Used only for a refused WRITE; every other source keeps its own copy.
   */
  readonly attachmentLimit?: unknown;
}

export interface DescribePlayerAIFailureInput extends PlayerAIFailureContext {
  readonly source: PlayerAIFailureSource;
  readonly code: unknown;
  readonly occurredAt?: unknown;
  /**
   * The SERVER's answer to "can this Run's request be asked again without
   * duplicating anything?", for `source: "run"` only.
   *
   * Anything other than an explicit `true` withholds the retry, so a client
   * reading an older server — or one that has not yet confirmed — offers the
   * review copy instead of a Try again the platform cannot vouch for.
   */
  readonly runRetrySafe?: boolean;
}

/**
 * The one place a Player AI failure becomes something a person can read.
 *
 * `code` is validated with {@link canonicalToken} before it is looked up, so a
 * payload carrying a sentence — or an object — in the code position lands on
 * `unknown` instead of being echoed into a diagnostics block.
 */
export function describePlayerAIFailure(
  input: DescribePlayerAIFailureInput,
): PlayerAIFailure {
  const code = canonicalToken(input.code) ?? "UNKNOWN";
  const family = playerAIFailureFamily(code);
  const base = FAMILY_COPY[family];
  const attachmentLimit =
    family === "refused" && input.source === "write"
      ? counter(input.attachmentLimit)
      : undefined;
  const sourced =
    code === RUN_DEFERRED_CODE
      ? RUN_DEFERRED_COPY
      : attachmentLimit === undefined
        ? sourceCopy(family, base, code, input.source)
        : messageAttachmentLimitCopy(attachmentLimit);
  /*
   * The catalogue decides what WENT WRONG; only the server can decide whether
   * asking again is safe. A Run failure whose family would offer a retry is
   * downgraded to the review copy unless the server has confirmed it — so the
   * card never says "Try again" with no button, and never offers one over work
   * a second ask could duplicate.
   */
  const copy =
    input.source === "run" &&
    sourced.action === "retry" &&
    input.runRetrySafe !== true
      ? FAMILY_COPY.unverified_outcome
      : sourced;
  const diagnostics: PlayerAIFailureDiagnostics = {
    errorCode: code,
    family,
    stage: STAGE_BY_SOURCE[input.source],
    retrySafety: copy.retrySafety,
    occurredAt: timestamp(input.occurredAt) ?? new Date().toISOString(),
    ...optional("runId", identifier(input.runId)),
    ...optional("proposalId", identifier(input.proposalId)),
    ...optional("requestId", identifier(input.requestId)),
    ...optional("httpStatus", httpStatus(input.httpStatus)),
    ...optional("attemptCount", counter(input.attemptCount)),
    ...optional("runOutcome", canonicalToken(input.runOutcome)),
    // An IDENTIFIER, not a canonical token: `runtime_release` is set per
    // deployment and a version commonly leads with a digit (`2026.09.16-1`),
    // which the enum shape rejects. Bounded the same way; it is still never
    // free text.
    ...optional("runtimeRelease", identifier(input.runtimeRelease)),
    ...optional("surface", surface(input.surface)),
    ...optional(
      "reason",
      knownReason(
        attachmentLimit === undefined
          ? input.reason
          : MESSAGE_ATTACHMENT_LIMIT_REASON,
        code,
      ),
    ),
  };
  return {
    family,
    title: copy.title,
    message: copy.message,
    action: copy.action,
    retrySafety: copy.retrySafety,
    diagnostics,
  };
}

function optional<K extends string, V>(
  key: K,
  value: V | undefined,
): Record<K, V> | Record<string, never> {
  return value === undefined ? {} : ({ [key]: value } as Record<K, V>);
}

/**
 * Classify an arbitrary thrown value into a code this catalogue can look up.
 *
 * The important half is what it does NOT do: an `Error` contributes its TYPE,
 * never its message. `fetch` rejects with `TypeError: Failed to fetch` on a
 * transport failure and with wholly different wording per browser and per
 * proxy, and that string is exactly the kind of text that used to be rendered
 * verbatim.
 */
export function playerAIFailureCode(error: unknown): {
  readonly code: string;
  readonly requestId?: string;
  readonly httpStatus?: number;
  readonly attachmentLimit?: number;
} {
  if (isProblemLike(error)) {
    const problem = error.problem;
    return {
      code: canonicalToken(problem.code) ?? "UNKNOWN",
      ...optional("requestId", identifier(problem.request_id)),
      ...optional("httpStatus", httpStatus(problem.status)),
      ...optional("attachmentLimit", attachmentLimitOf(problem)),
    };
  }
  // A `TypeError` out of `fetch` is the transport itself failing: no response
  // was received, so there is no status and nothing was accepted.
  if (error instanceof TypeError) return { code: "NETWORK_UNAVAILABLE" };
  return { code: "UNKNOWN" };
}

interface ProblemLike {
  readonly problem: {
    readonly code?: unknown;
    readonly status?: unknown;
    readonly request_id?: unknown;
    readonly fields?: unknown;
  };
}

/**
 * The file limit a 422 names, or undefined when it is not that refusal.
 *
 * Only `too_long` on a location ending in `attachment_ids` (the messages route
 * and a run's message trigger) is the count limit. A duplicate id
 * (`value_error`) or a malformed one (`uuid_parsing` on `attachment_ids.3`) is
 * a different refusal and keeps its own copy. An older server sends no
 * `limit`; the location and type already prove which limit it was, so the
 * contract mirror names it.
 */
function attachmentLimitOf(
  problem: ProblemLike["problem"],
): number | undefined {
  if (problem.code !== "VALIDATION_FAILED" || !Array.isArray(problem.fields)) {
    return undefined;
  }
  for (const field of problem.fields as unknown[]) {
    if (typeof field !== "object" || field === null) continue;
    const { path, type, limit } = field as {
      path?: unknown;
      type?: unknown;
      limit?: unknown;
    };
    if (
      type === "too_long" &&
      typeof path === "string" &&
      path.split(".").at(-1) === "attachment_ids"
    ) {
      return counter(limit) ?? MAX_MESSAGE_ATTACHMENTS;
    }
  }
  return undefined;
}

function isProblemLike(value: unknown): value is ProblemLike {
  if (typeof value !== "object" || value === null) return false;
  const problem = (value as { problem?: unknown }).problem;
  return typeof problem === "object" && problem !== null;
}

/** Field order of the copied block. Stable, so two reports are comparable. */
const DETAIL_ROWS: ReadonlyArray<
  readonly [label: string, key: keyof PlayerAIFailureDiagnostics]
> = [
  ["Code", "errorCode"],
  ["Category", "family"],
  ["Stage", "stage"],
  ["Reason", "reason"],
  ["Retry safety", "retrySafety"],
  ["Occurred at", "occurredAt"],
  ["Surface", "surface"],
  ["Run ID", "runId"],
  ["Proposal ID", "proposalId"],
  ["Request ID", "requestId"],
  ["HTTP status", "httpStatus"],
  ["Attempts", "attemptCount"],
  ["Run outcome", "runOutcome"],
  ["Release", "runtimeRelease"],
];

/**
 * The plain-text block behind "Copy error details".
 *
 * Absent fields are OMITTED rather than rendered as `unknown` or `-`: a support
 * reader must be able to tell "this deployment did not report a release" from
 * "the release was literally unknown", and a placeholder destroys that
 * difference. The message is included because it is what the user saw.
 */
export function playerAIFailureDetails(failure: PlayerAIFailure): string {
  const lines = [
    "Player AI error",
    `Message: ${failure.title} ${failure.message}`,
  ];
  for (const [label, key] of DETAIL_ROWS) {
    const value = failure.diagnostics[key];
    if (value === undefined) continue;
    lines.push(`${label}: ${String(value)}`);
  }
  return lines.join("\n");
}
